import React, { useState, useMemo, useEffect } from "react";
import CommonCard from "../../../global/card";
import CommonSkeletonLoader from "../../../global/loading";
import CommonPagination from "../../../global/pagination";
import useDebounce from "../../../../utils/customHooks";
import { QUERY_KEYS } from "../../../../lib/query-constants";
import { useGenericQuery } from "../../../../queries/use-generic-query";
import SOURCES from "../../../../utils/constants";
import EnhancedPaymentHistoryList from "./enhanced-payment-history-list";

const EnhancedPaymentHistory = () => {
    const { DEFAULT_RECORDS_LISTING } = SOURCES;

    const [searchInput, setSearchInput] = useState("");
    const [paymentHistoryViewType, setPaymentHistoryViewType] = useState(() => {
        return localStorage.getItem("paymentHistoryViewType") || "GRID";
    });
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortOrder, setSortOrder] = useState(-1); // Default to newest first

    const [pagination, setPagination] = useState({
        offset: 0,
        limit: SOURCES.DEFAULT_RECORDS_LISTING,
        page: 1,
    });

    const debouncedSearchTerm = useDebounce(searchInput, 500);

    const commonParams = useMemo(() => ({
        offset: pagination.offset,
        limit: pagination.limit,
        sortBy,
        sortOrder,
    }), [pagination.offset, pagination.limit, sortBy, sortOrder]);

    // Regular history fetch
    const {
        data: paymentsData,
        isLoading: isPaymentsLoading,
        isFetching: isPaymentsFetching,
    } = useGenericQuery({
        endpoint: "payment/history",
        queryKey: QUERY_KEYS.paymentHistory.lists(commonParams),
        queryParams: commonParams,
        enabled: !debouncedSearchTerm,
    });

    // Search history fetch
    const {
        data: searchData,
        isInitialLoading: isSearchLoading,
    } = useGenericQuery({
        endpoint: "payment/history-search",
        queryKey: QUERY_KEYS.paymentHistory.lists({ q: debouncedSearchTerm, ...commonParams }),
        queryParams: { q: debouncedSearchTerm, ...commonParams },
        enabled: !!debouncedSearchTerm && debouncedSearchTerm.length >= 2,
    });

    const isLoading = isPaymentsLoading || isSearchLoading;

    const getFinalData = useMemo(() => {
        if (debouncedSearchTerm && searchData) {
            return searchData.history || searchData || [];
        }
        return paymentsData?.history || paymentsData || [];
    }, [paymentsData, searchData, debouncedSearchTerm]);

    const totalRecords = useMemo(() => {
        if (debouncedSearchTerm && searchData) {
            return searchData.count || (Array.isArray(searchData) ? searchData.length : 0);
        }
        return paymentsData?.count || (Array.isArray(paymentsData) ? paymentsData.length : 0);
    }, [paymentsData, searchData, debouncedSearchTerm]);

    return (
        <CommonCard
            isSearch={{
                show: true,
                placeholder: "Search payment history...",
                searchInput: searchInput,
                setSearchInput: setSearchInput
            }}
            isListGridView={{
                show: true,
                viewType: paymentHistoryViewType,
                setViewType: setPaymentHistoryViewType,
                viewFor: "paymentHistoryViewType"
            }}
            isLoading={{
                show: isLoading,
                loaderCmp: (
                    <CommonSkeletonLoader
                        type={paymentHistoryViewType === "GRID" ? "card" : "table"}
                        tableLength={5}
                    />
                ),
            }}
            className={(!isLoading && isPaymentsFetching) && "opacity-50"}
        >
            <EnhancedPaymentHistoryList
                isLoading={isLoading}
                history={getFinalData}
                paymentHistoryViewType={paymentHistoryViewType}
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={(field) => {
                    setSortBy(field);
                    setSortOrder(sortOrder === 1 ? -1 : 1);
                }}
            />

            {(totalRecords > 0 && !searchInput) && (
                <CommonPagination
                    currentPage={pagination.page}
                    defaultLimit={pagination.limit}
                    totalRecords={totalRecords}
                    setCurrentPage={setPagination}
                    activePageRecords={
                        Array.isArray(getFinalData) ? getFinalData.length : 0
                    }
                    paginationFor="Payments"
                    className="mt-4"
                />
            )}
        </CommonCard>
    );
};

export default EnhancedPaymentHistory;